import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useDrag } from 'react-dnd';
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import createCache from "@emotion/cache";
import { NonceProvider } from "react-select";
import { CacheProvider } from '@emotion/react';
import useTicketSuffix from "@/features/task/hooks/useTicketSuffix";
import { getAttachmentUrl } from "@/features/attachments/services/attachmentService";
const TaskCard = ({ task, onAdvance, loading }) => {
    //const loading = loadingTaskId === task.id;
    const [, dragRef] = useDrag({
        type: 'TASK',
        item: task, // Les données associées à l'élément draggable
    });
    const { formatTicketCode } = useTicketSuffix();
    const nonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content') ?? '';
    const cache = createCache({ key: 'react-select', nonce });
    const [assigneePhotoUrls, setAssigneePhotoUrls] = React.useState({});
    const getAssigneeKey = (assignee) => {
        return String(assignee.user_id ?? assignee.id ?? assignee.profile_photo?.id ?? 'unknown');
    };
    const getAssigneeProfilePhotoUrl = (assignee) => {
        const assigneeKey = getAssigneeKey(assignee);
        if (assigneePhotoUrls[assigneeKey]) {
            return assigneePhotoUrls[assigneeKey];
        }
        return assignee.profile_photo?.file_path;
    };
    const getAssigneeInitials = (assignee) => {
        const firstInitial = assignee.first_name?.charAt(0) ?? '';
        const lastInitial = assignee.last_name?.charAt(0) ?? '';
        const initials = `${firstInitial}${lastInitial}`.trim();
        return initials || assignee.first_name?.charAt(0) || '?';
    };
    const parseDate = (dateStr) => {
        if (!dateStr) {
            return null;
        }
        const timestamp = Date.parse(dateStr);
        if (!Number.isNaN(timestamp)) {
            return new Date(timestamp);
        }
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
        if (!match) {
            return null;
        }
        const [, year, month, day] = match;
        return new Date(Number(year), Number(month) - 1, Number(day), 23, 59, 59, 999);
    };
    const isTaskLate = React.useMemo(() => {
        const now = new Date();
        const dueDate = parseDate(task.due_date);
        if (dueDate) {
            return dueDate.getTime() < now.getTime();
        }
        const createdAt = parseDate(task.created_at);
        if (!createdAt) {
            return false;
        }
        const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
        return createdAt.getTime() + twoDaysInMs < now.getTime();
    }, [task.created_at, task.due_date]);
    const formatDueDate = (dateStr) => {
        if (!dateStr)
            return "";
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
        if (match) {
            const [, y, m, d] = match;
            return `${d}/${m}/${y}`;
        }
        const d = new Date(dateStr);
        if (isNaN(d.getTime()))
            return dateStr;
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };
    React.useEffect(() => {
        if (!task.assignees)
            return;
        try {
            const photoUrlMap = task.assignees.reduce((acc, assignee) => {
                const photoPath = assignee.profile_photo?.file_path;
                if (!photoPath)
                    return acc;
                const key = getAssigneeKey(assignee);
                acc[key] = getAttachmentUrl(photoPath);
                return acc;
            }, {});
            setAssigneePhotoUrls(photoUrlMap);
        }
        catch (error) {
            console.error("Erreur lors de la préparation des URLs des photos de profils des assignés :", error);
        }
    }, [task.assignees]);
    return (_jsx(NonceProvider, { nonce: nonce, cacheKey: "react-select", children: _jsx(CacheProvider, { value: cache, children: _jsxs("div", { className: `bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow transition-transform hover:-translate-y-0.5 cursor-pointer p-4`, ref: dragRef, children: [_jsxs(Link, { to: `/tasks/${task.id}`, children: [_jsxs("div", { className: "flex items-start justify-between mb-3", children: [_jsx("h3", { className: `font-medium text-gray-900  text-base`, children: _jsx("span", { className: "text-blue-600 text-size-1", children: formatTicketCode(task.id) }) }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("span", { className: `px-2 py-1 text-xs font-medium rounded-full bg-${task.priority?.color}-500 opacity-60`, children: task.priority?.label }), isTaskLate && (_jsx("span", { className: "flex items-center text-xs font-semibold text-red-600", title: "T\u00E2che en retard", children: _jsx(AlertTriangle, { className: "mr-1 h-4 w-4", "aria-hidden": "true" }) }))] })] }), _jsx("div", { className: "p-1", children: _jsx("div", { className: "min-h-[5rem]", children: _jsx("p", { children: task.title }) }) })] }), _jsxs("div", { className: "flex items-start justify-between", children: [_jsx("div", { className: "flex items-center justify-between", children: _jsx("div", { className: "flex items-center space-x-2", children: _jsxs("div", { className: "flex -space-x-1", children: [task.assignees?.slice(0, 3).map((assignee) => (_jsx("div", { title: `${assignee.first_name ?? ''} ${assignee.last_name ?? ''}`.trim(), children: (() => {
                                                    const profilePhotoUrl = getAssigneeProfilePhotoUrl(assignee);
                                                    const initials = getAssigneeInitials(assignee);
                                                    if (profilePhotoUrl) {
                                                        return (_jsx("img", { src: profilePhotoUrl, alt: initials, className: "h-6 w-6 rounded-full object-cover border-2 border-white" }));
                                                    }
                                                    return (_jsx("div", { className: "h-6 w-6 bg-blue-500 rounded-full flex items-center justify-center text-xs text-white font-medium border-2 border-white", "aria-label": initials, children: initials }));
                                                })() }, getAssigneeKey(assignee)))), task.assignees && task.assignees?.length > 3 && (_jsxs("div", { className: "h-6 w-6 bg-gray-400 rounded-full flex items-center justify-center text-xs text-white font-medium border-2 border-white", children: ["+", task.assignees?.length - 3] }))] }) }) }), _jsx("div", { children: _jsx("span", { className: `text-xs ${isTaskLate ? 'text-red-600 font-semibold' : 'text-gray-600'}`, children: formatDueDate(task.due_date) }) }), _jsx("div", { children: _jsx("span", { className: "text-xs text-gray-600", children: task.ville_label }) })] })] }, task.id) }) }));
};
export default TaskCard;
